import React, { useEffect, useState } from 'react'
import { graphqlFetch } from '@pylot-data/graphqlFetch'
import { generateSignifydSessionId } from '@corratech/pylot-signifyd/src/graphql/generateSignifydSessionId'
import { useCart } from '@corratech/pylot-cart-manager'
import { useSignifydContext } from './hooks/useSignifydContext'

const SIGNIFYD_CDN_URL = 'https://cdn-scripts.signifyd.com/api/script-tag.js'
const STORAGE_KEY = 'sig_api'

type SessionIdOutput = {
    data_order_session_id: string
}
type SessionIdInput = {
    cart_id: string
}
type SessionIdOutputGql = {
    generateSignifydSessionId: SessionIdOutput
}

declare global {
    interface Window {
        SIGNIFYD_GLOBAL?: {
            scriptTagHasLoaded: () => boolean
        }
    }
}

const Signifyd = (): JSX.Element | null => {
    const { data } = useCart()
    const [isLoading, setIsLoading] = useState(false)
    const [sessionId, setSessionId] = useState<string>('')
    const { shouldLoadSignifyd } = useSignifydContext()

    const generateSessionId = async () => {
        setIsLoading(true)
        const response = graphqlFetch<SessionIdInput, SessionIdOutputGql>({
            query: generateSignifydSessionId,
            variables: {
                cart_id: data?.data?.cart?.id || ''
            }
        })

        setSessionId(
            (await response).data.generateSignifydSessionId
                .data_order_session_id
        )
        setIsLoading(false)
    }

    useEffect(() => {
        if (data?.data?.cart?.id) {
            let generate = true
            const localData = window.localStorage.getItem(STORAGE_KEY)
            if (localData != null) {
                const parsedData = JSON.parse(localData)
                if (parsedData?.cartId) {
                    if (parsedData?.cartId === data?.data?.cart?.id) {
                        setSessionId(parsedData.sessionOrderId)
                        generate = false
                    }
                }
            }

            if (generate === true) {
                generateSessionId()
            }
        }
    }, [data?.data?.cart?.id])

    useEffect(() => {
        const id = 'sig-api'
        const existing = document.getElementById(id)
        if (!shouldLoadSignifyd) {
            existing?.remove()
            return
        }
        if (!sessionId || isLoading) return
        if (existing) {
            existing.setAttribute('data-order-session-id', sessionId)
            return
        }
        const script = document.createElement('script')
        script.id = id
        script.src = SIGNIFYD_CDN_URL
        script.type = 'text/javascript'
        script.async = true
        script.setAttribute('data-order-session-id', sessionId)
        document.head.appendChild(script)
        return () => {
            document.getElementById(id)?.remove()
        }
    }, [shouldLoadSignifyd, sessionId, isLoading])

    useEffect(() => {
        if (sessionId !== '') {
            if (typeof window !== 'undefined') {
                window.localStorage.setItem(
                    STORAGE_KEY,
                    JSON.stringify({
                        cartId: data?.data?.cart?.id,
                        sessionOrderId: sessionId
                    })
                )
            }
        }
    }, [sessionId])

    return null
}

export default Signifyd
